import { useQuery } from "@apollo/client";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Alert, CircularProgress, IconButton, Popover, Stack, Typography } from "@mui/material";
import { WasteAccumulationAreaType } from "generated-graphql/graphql";
import { MouseEvent, useState } from "react";

import { FACILITY_ACCUMULATION_RULESET_QUERY } from "./api";
import { formatCapsSlotLine, formatClockSlotLine } from "./rulesetSlotLine";

/**
 * "Why this limit" popover: an info button that, on open, fetches the
 * facility's standing accumulation ruleset for one area type and explains
 * the clock/caps axes with their citations. Shows the standing ruleset
 * only — the resolver pins the episodic axis off, which is correct here
 * since this surface isn't scoped to any one container's episodic window.
 */
export function RulesetPopover({
  facilityId,
  areaType,
}: {
  facilityId: string;
  areaType: WasteAccumulationAreaType;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_RULESET_QUERY, {
    variables: { facilityId, areaType },
    skip: !open,
  });

  const ruleset = data?.facility?.accumulationRuleset;

  return (
    <>
      <IconButton
        size="small"
        aria-label="Why this limit"
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
      >
        <InfoOutlined fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack spacing={1} sx={{ p: 2, maxWidth: 360 }}>
          <Typography variant="subtitle2">Why this limit</Typography>
          {loading && !ruleset && <CircularProgress size={20} />}
          {ruleset?.undetermined && (
            <Alert severity="warning" variant="outlined">
              {ruleset.undetermined.reason}
            </Alert>
          )}
          {ruleset && !ruleset.undetermined && (
            <>
              {ruleset.clock && (
                <Typography variant="body2">{formatClockSlotLine(ruleset.clock)}</Typography>
              )}
              {ruleset.caps && (
                <Typography variant="body2">{formatCapsSlotLine(ruleset.caps)}</Typography>
              )}
              {!ruleset.clock && !ruleset.caps && (
                <Typography variant="body2" color="text.secondary">
                  No standing limit applies.
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Popover>
    </>
  );
}
